/**
 * Maps the active i18next language code to a date-fns `Locale` object so
 * date formatters (`format`, `formatDistanceToNow`, `formatRelative`, …)
 * render weekday and month names in the user's chosen language.
 *
 * Pattern:
 *   import { dfLocale } from '@/lib/date-fns-locale';
 *   format(date, 'do MMMM yyyy', { locale: dfLocale() });
 *   formatDistanceToNow(date, { addSuffix: true, locale: dfLocale() });
 *
 * The lookup is read fresh on each call (no React subscription) so components
 * already re-rendering on `i18n.language` change get the new locale on the
 * next render without extra wiring. For components that don't subscribe to
 * i18n changes, pull `i18n.language` via `useTranslation()` and use it as a
 * dep on the relevant `useMemo` so the formatter result refreshes too.
 *
 * Keyed by the codes in `@/lib/languages` and resolved through the same
 * `resolveLanguage` the switcher uses, so `pt-BR` gets Brazilian formatting
 * rather than collapsing onto European `pt`.
 */
import type { Locale } from 'date-fns';
import { ar, de, enUS, es, fr, pt, ptBR, zhCN } from 'date-fns/locale';

import i18n from '@/lib/i18n';
import { resolveLanguage } from '@/lib/languages';

const LOCALES: Record<string, Locale> = {
    en: enUS,
    ar,
    'pt-BR': ptBR,
    zh: zhCN,
    fr,
    de,
    pt,
    es,
};

export function dfLocale(): Locale {
    return LOCALES[resolveLanguage(i18n.language).code] ?? enUS;
}
